/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from "react";
import t from "../../../Common/utils/translation/translator";
import LabelsPicker from "../../../Common/components/Inputs/LabelsPicker/LabelsPicker/LabelsPicker";
import CustomPopup from "../../../Common/components/CustomPopup/CustomPopup";
import SncWidget from "../SpecifyAndSelect/SncWidget";
import PropTypes from "prop-types";
import Navigator from "../../../Common/layouts/Navigator/Navigator";
import {
  collapseAxis,
  deleteAxis,
  duplicateAxis,
  selectIntegratedDrivesConfiguration,
  setBundle,
  setIntegratedDrivesAxisFilters,
  setSncWidgetOutputReducer,
  updateAxisMotorType,
  updateAxisName
} from "../../../IntegratedDrives/store/slices/integrated_drives";
import { useDispatch, useSelector } from "react-redux";
import EditableAxisName from "../../../Retrofit/components/Axes/EditableAxisName";
import { selectConfigurationType, updateConfiguration } from "../../../Common/store/slices/common";
import FiltersWrapper from "../../../Retrofit/components/Devices/FiltersWrapper";
import { choosableColumns, defaultColumns } from "../../../IntegratedDrives/store/data/integratedDrivesData";
import { useGetIntegratedDrivesBundles } from "../useGetIntegratedDrivesBundles";
import OptionsSelector from "shared-components/lib/options_selector";
import { getProductImgUrl } from "../../../Common/utils/api/api";
import BundleItemInfo from "../../../Retrofit/components/Devices/BundleItemInfo";
import ProductDataSheetLink from "../../../Common/components/Shared/ProductDataSheetLink";
import { tData } from "../../../Common/utils/translation/dataUtils";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import CorrespondingCrossComponent from "../../../Retrofit/components/Devices/CorrespondingCrossComponent/CorrespondingCrossComponent";
import { selectRowsToShow } from "../../../Retrofit/store/slices/retrofit";
import integratedDriveImage from "../../../IntegratedDrives/Assets/integrated-drive.png";

const AxisComponent = ({ axisId, axisName, motorType, bundle, collapsed, storedSncWidgetOutput, filters }) => {
  const dispatch = useDispatch();
  const configuration = useSelector(selectIntegratedDrivesConfiguration);

  // Popups
  const [popupOpened, setPopupOpened] = useState(false);
  const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false);

  const [sncWidgetOutput, setSncWidgetOutput] = useState(storedSncWidgetOutput);
  const [specifyConfigureStep, setSpecifyConfigureStep] = useState("SPECIFY");

  // Load SNC CSS and JS if not already loaded
  useEffect(() => {
    const linkElement = document.getElementById("SNC_CSS_ID");

    if (!linkElement) {
      const newLink = document.createElement("link");
      newLink.id = "SNC_CSS_ID";
      newLink.rel = "stylesheet";
      newLink.href = "https://selectandconfig-widget.schneider-electric.com/release/wrapper/2.X/wrapper.min.css";
      document.head.appendChild(newLink);
    }

    const scriptElement = document.getElementById("SNC_SCRIPT_ID");

    if (!scriptElement) {
      const newScript = document.createElement("script");
      newScript.id = "SNC_SCRIPT_ID";
      newScript.type = "text/javascript";
      newScript.src = "https://selectandconfig-widget.schneider-electric.com/release/wrapper/2.X/wrapper.min.js";
      document.head.appendChild(newScript);
    }
  }, []);

  // update the filters with the selection from the SncWidget
  useEffect(() => {
    const extractedIntegratedDriveFiltersAsArray =
      sncWidgetOutput?.extras?.characteristicsSummary[0].characteristicGroups
        .map((charGroup) => charGroup.characteristics)
        ?.flat();
    // turn this array to an object
    const extractedIntegratedDriveFilters = extractedIntegratedDriveFiltersAsArray?.reduce((acc, item) => {
      const { id, values } = item;
      acc[id] = values.map((value) => value.valueId);
      return acc;
    }, {});
    dispatch(
      setIntegratedDrivesAxisFilters({
        axisId: axisId,
        filters: {
          ...filters,
          integratedDriveFilter: {
            ...extractedIntegratedDriveFilters,
            MOTOR_TYPE: motorType === t("integrated_drives.ui.motor-type.servo") ? "SERVO" : "STEPPER"
          }
        }
      })
    );
  }, [sncWidgetOutput]);

  const handleSetMotorType = (val) => {
    dispatch(updateAxisMotorType({ axisId, motorType: val }));
  };

  const handleSetWidgetOutput = (output) => {
    setSncWidgetOutput(output);
    dispatch(setSncWidgetOutputReducer({ axisId, sncWidgetOutput: output }));
  };

  useEffect(() => {
    setSpecifyConfigureStep("CONFIGURE");
  }, [sncWidgetOutput]);

  const handleUpdateAxisName = (newName) => {
    if (!newName) {
      return;
    }
    dispatch(updateAxisName({ axisId: axisId, name: newName }));
    dispatch(updateConfiguration());
  };

  const handleCollapseAxis = () => {
    dispatch(collapseAxis({ axisId }));
    dispatch(updateConfiguration());
  };

  const handleDeleteAxis = () => {
    dispatch(deleteAxis({ axisId }));
    setShowDeleteConfirmationPopup(false);
  };

  const handleDuplicateAxis = () => {
    dispatch(duplicateAxis({ axisId }));
    dispatch(updateConfiguration());
  };

  // Get fetched (and filtered) bundles from redux
  // const fetchedBundles = useSelector(selectFetchedBundles);

  const rowsToShow = useSelector(selectRowsToShow);
  const [paging, setPaging] = useState({ skip: 0, limit: rowsToShow });

  const resetPaging = () => {
    setPaging({ ...paging, skip: 0 });
  };

  const [orderBy, setOrderBy] = useState({
    // TBD
    attributeName: "nominalTorque",
    sort: "asc"
  });

  const {
    refetch: fetchIntegratedDrivesBundles,
    isFetching: isFetchingIntegratedDrivesBundles,
    bundles: integratedDrivesBundles,
    countOfBundles
  } = useGetIntegratedDrivesBundles(filters, paging, resetPaging, orderBy, sncWidgetOutput);

  // useEffect(() => {
  //   fetchIntegratedDrivesBundles();
  //   console.log("NEW FILTERS", filters?.gearboxFilter?.ratio);
  // }, [filters]);

  const handleSelectBundle = (bundle) => {
    dispatch(setBundle({ axisId, bundle }));
    dispatch(updateConfiguration());
  };

  const [calculatedDefaultColumns, setCalculatedDefaultColumns] = useState(defaultColumns);
  const configurationType = useSelector(selectConfigurationType);

  useEffect(() => {
    // whenever sncWidgetOutput changes, update the default columns
    if (sncWidgetOutput) {
      const newDefaultColumns = defaultColumns.map((group) => {
        if (group.groupName === "integrated_drive") {
          return {
            ...group,
            columns: [
              ...group.columns,
              ...["PRODUCT_CODE"],
              ...sncWidgetOutput.extras.characteristicsSummary[0].characteristicGroups
                .map((charGroup) => charGroup.characteristics)
                .flat()
                .map((char) => char.id)
            ]
          };
        }
        return group;
      });
      setCalculatedDefaultColumns(newDefaultColumns);
      localStorage.setItem(
        "motion-configurator-" + configurationType + "-table-columns",
        JSON.stringify(newDefaultColumns)
      );
    }
  }, [sncWidgetOutput]);

  const mapToStringWithSeparator = (map, separator = "|") => {
    // console.log("mapToStringWithSeparator");
    const itemComponents = [];
    const entries = Array.from(map.entries());
    // eslint-disable-next-line no-restricted-syntax
    for (const entry of entries) {
      const [key, value] = entry;
      itemComponents.push(<BundleItemInfo infoKey={key} infoValues={value} key={key} />);
      if (entry !== entries[entries.length - 1]) {
        itemComponents.push(<span className="text-gray-700">{separator}</span>);
      }
    }
    return <div className="flex flex-row items-start gap-4 font-light text-sm">{itemComponents}</div>;
  };

  const styledProductDataSheetLink = (idLabel, eventTag, reference, preferredReference, ean) => (
    <div className="flex flex-col flex-1 ml-2">
      <div
        className="font-bold text-xs print:text-xxs print:underline hover:underline text-secondary hover:text-darksecondary"
        rel="noreferrer"
      >
        <ProductDataSheetLink idLabel={idLabel} eventTag={eventTag} reference={reference} />
      </div>
      {(preferredReference || ean) && (
        <span className="text-xs text-gray-600">
          <span className="font-semibold">{preferredReference}</span>
          {ean}
        </span>
      )}
    </div>
  );

  const generatekeyValueDescription = (type) => {
    // console.log("generatekeyValueDescription");
    const descElements = new Map();
    switch (type) {
      case "gearbox":
        descElements.set(t("msizer.ui.gearbox_filters.short_name"), [
          `${tData("gearbox", "family", bundle.gearbox?.family)}`
        ]);
        descElements.set(t("msizer.ui.gearbox_filters.reduction_ratio"), [`${bundle.gearbox?.reductionRatio}:1`]);
        descElements.set(t("msizer.ui.efficiency"), [`${bundle.gearbox?.efficiency}%`]);
        break;
      case "adaptationKit":
        descElements.set(t("msizer.ui.gearbox_filters.short_name"), [
          `${tData("gearbox", "family", bundle.gearbox?.family)}`
        ]);
        descElements.set(t("msizer.ui.gearbox_filters.reduction_ratio"), [`${bundle.gearbox?.reductionRatio}:1`]);
        descElements.set(t("msizer.ui.efficiency"), [`${bundle.gearbox?.efficiency}%`]);
        break;
      case "integratedDrive":
        descElements.set(t("integrated_drives.ui.technology-type"), [motorType]);

        descElements.set(t("msizer.ui.integrated_drives_attributes.drive_type"), [
          bundle.integratedDrive?.extras?.characteristicsSummary?.[0]?.characteristicGroups?.[1]?.characteristics?.filter(
            (char) => char.id === "T_OFFER_TYPE"
          )?.[0]?.values?.[0]?.value
        ]);
        descElements.set(t("msizer.ui.integrated_drives_attributes.motor_flange_size"), [
          bundle.integratedDrive?.extras?.characteristicsSummary?.[0]?.characteristicGroups?.[1]?.characteristics?.filter(
            (char) => char.id === "T_MOTOR_FLANGE_SIZE"
          )?.[0]?.values?.[0]?.value + " mm"
        ]);
        break;
      default:
    }
    return descElements;
  };

  const handleApplyFilters = (axisId, newFilters) => {
    console.log("handleApplyFilters", axisId, newFilters);
    dispatch(
      setIntegratedDrivesAxisFilters({
        axisId: axisId,
        filters: { ...filters, gearboxFilter: newFilters.gearboxFilter }
      })
    );
    resetPaging();
  };

  return (
    <div
      key={axisId}
      className={`w-full border-qds p-4 flex flex-col border-solid
      ${bundle ? "!border-l-primary !border-l-2" : ""}`}
    >
      <div className="flex justify-between items-center">
        <div className="flex flex-col space-y-2">
          <EditableAxisName
            axisName={axisName}
            onConfirm={(newName) => {
              handleUpdateAxisName(newName);
            }}
            configId={configuration?.configId}
          />
          {!bundle && (
            <LabelsPicker
              idLabel=".."
              label={t("integrated_drives.ui.motor-type.title")}
              cols={2}
              options={[
                { label: t("integrated_drives.ui.motor-type.servo"), key: t("integrated_drives.ui.motor-type.servo") },
                {
                  label: t("integrated_drives.ui.motor-type.stepper"),
                  key: t("integrated_drives.ui.motor-type.stepper")
                }
              ]}
              selectedLabelsInit={[motorType]}
              onChange={(val) => {
                handleSetMotorType(val?.[0]);
              }}
              changeWithInit
              disabledLabels={[]}
              singleChoice
            />
          )}
        </div>
        <div className="flex space-x-2 items-center">
          {!bundle && (
            <qds-button
              size="small"
              importance="emphasized"
              text={t("integrated_drives.ui.apply-and-configure")}
              onClick={() => {
                setSpecifyConfigureStep("SPECIFY");
                setPopupOpened(true);
              }}
              {...(motorType === null || motorType === undefined ? { disabled: "" } : {})}
            />
          )}
          {bundle && (
            <qds-button
              size="small"
              text={t("msizer.ui.select.change_product_bundle_button_text")}
              onClick={() => {
                // set the step and configure the bundle
                setSpecifyConfigureStep("CONFIGURE");
                setPopupOpened(true);
              }}
            />
          )}
          {bundle && (
            <qds-button
              size="small"
              id={`specify-button-duplicate-${axisId}`}
              title={t("msizer.ui.tooltip.duplicate_axis")}
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateAxis();
              }}
              icon-name="pages-double"
            ></qds-button>
          )}
          <qds-button
            size="small"
            id={`specify-button-delete-${axisId}`}
            title={t("msizer.ui.tooltip.delete_axis")}
            {...(configuration?.configData.axes.length <= 1 ? { disabled: "" } : {})}
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirmationPopup(true);
            }}
            icon-name="trash-can"
          ></qds-button>
          {bundle && (
            <div
              className="mx-2 items-center cursor-pointer"
              onClick={() => {
                handleCollapseAxis();
              }}
            >
              {collapsed && <FiChevronDown className="text-gray-500" />}
              {!collapsed && <FiChevronUp className="text-gray-500" />}
            </div>
          )}
        </div>
      </div>
      {bundle && collapsed && (
        <div>
          {/* <div className="border shadow mt-20">
            BUNDLE SELECTED: <pre>{JSON.stringify(bundle, undefined, 2)}</pre>
          </div> */}
          {["integratedDrive", "gearbox", "adaptationKit"].map((type) => {
            const descElements = generatekeyValueDescription(type);
            const reference = bundle?.[type]?.commercialReference || bundle?.[type]?.extras?.codification;
            return (
              bundle[type] && (
                <OptionsSelector
                  key={type}
                  moduleCategories={[
                    {
                      displayType: "flat",
                      categoryName: t(`msizer.ui.${type}`),
                      moduleItems: [
                        {
                          moduleId: reference,
                          imageUrl: type === "integratedDrive" ? integratedDriveImage : getProductImgUrl(reference),
                          renderDescriptionComponent: () => mapToStringWithSeparator(descElements),
                          deviceIdentification: {
                            identificationKey: reference
                          },
                          optionsAvailable: {
                            accessories: []
                          },
                          preventCardDisabledWhenNoOptions: true,
                          modules: [],
                          getOptions: () => {},
                          onAddOptions: () => {},
                          onDecreaseOptions: () => {},
                          onRemoveOptions: () => {},
                          onAddSubAccessories: () => {},
                          onDecreaseSubAccessories: () => {},
                          onRemoveSubAccessories: () => {}
                        }
                      ]
                    }
                  ]}
                  renderProductLink={(reference) =>
                    styledProductDataSheetLink(
                      `${type}-${axisId}`,
                      // eventsData.tags.specifySelectCompare,
                      null,
                      reference,
                      // partnerPreferences?.preferredProductReference,
                      null,
                      // get_EAN_ENUMBER(axis.bundle, type)
                      null
                    )
                  }
                  getLabel={(key) => t(`msizer.ui.${key}`)}
                  getAndReplaceLabel={() => {}}
                  mobileBreakpoint={800}
                />
              )
            );
          })}
        </div>
      )}

      {/* POPUPS */}
      <CustomPopup
        headerTitle={t("integrated_drives.ui.axis-parameters")}
        highlightHeaderTitle
        opened={popupOpened}
        // wideWidth
        fullScreen
        // noPadding={specifyConfigureStep === "SPECIFY"}
        onClose={() => {
          setPopupOpened(false);
        }}
        closeOnDocumentClick={false}
      >
        {specifyConfigureStep === "SPECIFY" && (
          <SncWidget
            onOutput={(output) => handleSetWidgetOutput(output)}
            kbType={motorType === t("integrated_drives.ui.motor-type.servo") ? "SERVO" : "STEPPER"}
            presetValues={storedSncWidgetOutput?.extras?.characteristicsSummary?.[0]?.characteristicGroups
              ?.map((charGroup) =>
                charGroup.characteristics.map((char) => {
                  return {
                    criteria: char.id,
                    value: char.values[0].valueId,
                    bomPath: char.bomPath
                  };
                })
              )
              ?.flat()}
          />
        )}
        {specifyConfigureStep === "CONFIGURE" && (
          <>
            <FiltersWrapper
              filters={filters}
              onApplyFilters={handleApplyFilters}
              motorTab={false}
              driveTab={false}
              axisId={axisId}
            />
            <CorrespondingCrossComponent
              filters={filters}
              selectedBundle={bundle}
              onSelectBundle={(bundle) => {
                handleSelectBundle(bundle);
                setPopupOpened(false);
              }}
              defaultColumns={calculatedDefaultColumns}
              choosableColumns={choosableColumns}
              bundles={integratedDrivesBundles}
              fetchBundles={fetchIntegratedDrivesBundles}
              isFetchingBundles={isFetchingIntegratedDrivesBundles}
              paging={paging}
              setPaging={setPaging}
              rowsToShow={rowsToShow}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              countOfBundles={countOfBundles}
              disableComparator
            />
            {/* <p className="text-xl">
              Product code : <b>{sncWidgetOutput?.extras?.codification}</b>
            </p>
            <pre className="text-left h-96 overflow-scroll border">{JSON.stringify(sncWidgetOutput, undefined, 2)}</pre> */}
            <div className="mt-10"></div>
            <Navigator
              // continueLabel={t("msizer.ui.footer.continue")}
              // continueAction={() => {}}
              continueButtonDisabled={true}
              previousAction={() => {
                setSpecifyConfigureStep("SPECIFY");
              }}
              rounded
            />
          </>
        )}
      </CustomPopup>

      <CustomPopup
        opened={showDeleteConfirmationPopup}
        onClose={() => setShowDeleteConfirmationPopup(false)}
        title={t("msizer.ui.popup.delete.axis.confirmation.title")}
        text={`${t("msizer.ui.popup.delete.axis.confirmation.text")} ${axisName}.`}
        cancelLabel={t("msizer.ui.popup.button.cancel")}
        cancelAction={
          () => setShowDeleteConfirmationPopup(false)
          // eslint-disable-next-line react/jsx-curly-newline
        }
        continueLabel={t("msizer.ui.popup.button.confirm")}
        continueAction={(e) => {
          e.stopPropagation();
          handleDeleteAxis();
        }}
      />
    </div>
  );
};

// props validation
AxisComponent.propTypes = {
  axisId: PropTypes.number.isRequired,
  axisName: PropTypes.string.isRequired,
  motorType: PropTypes.string
};

export default AxisComponent;
